import React, { useEffect } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme, useMediaQuery } from "@mui/material";
import { FlexContainer } from "../../../../shared/components/styled/Container.styled";
import FormItem from "../../../../shared/components/form/FormItem";
import InfoText from "../../../../shared/components/InfoText";
import {
  ESCROW_SHORTAGE,
  MAX_AMOUNT_INPUT_LENGTH,
  ONE_TIME_PAYMENT,
} from "../../../../shared/constants/Payments.constants";
import {
  checkValidity,
  prefillInput,
  padCents,
} from "../../../../shared/utils/utility-functions";
import { PillButton } from "../../../../shared/components/styled/Button.styled";
import { Box } from "@mui/material";
import { formatCurrency } from "../../../../shared/utils/currency";
import { format, isValid } from "date-fns";

export default function PaymentAmount({
  fieldsDisabled = true,
  selectedType = ONE_TIME_PAYMENT,
  amountDue = 0,
  dueDate = null,
  lateFeesDue = 0,
  feesDue = 0,
  amount = "",
  setAmount = () => {},
  lateFees = "",
  setLateFees = () => {},
  fees = "",
  setFees = () => {},
  additionalPrincipal = "",
  setAdditionalPrincipal = () => {},
  additionalEscrow = "",
  setAdditionalEscrow = () => {},
  amountErrorMessage = "",
  setAmountErrorMessage = () => {},
  lateFeesErrorMessage = "",
  setLateFeesErrorMessage = () => {},
  feesErrorMessage = "",
  setFeesErrorMessage = () => {},
  additionalPrincipalErrorMessage = "",
  setAdditionalPrincipalErrorMessage = () => {},
  additionalEscrowErrorMessage = "",
  setAdditionalEscrowErrorMessage = () => {},
  mandateLateFee = false,
  isPrincipalOnlyPayment = false,
  principalMaxTransaction = 0,
  principalDisabled = false,
  additionalEscrowAmountAllowed = true,
  helocOrConstructionLoan = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (
      isPrincipalOnlyPayment &&
      principalMaxTransaction &&
      +additionalPrincipal > +principalMaxTransaction
    ) {
      setAdditionalPrincipal(principalMaxTransaction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrincipalOnlyPayment]);

  const handleInputChangeWithMaxValue = (
    e,
    maxValue,
    setter,
    setErrorMessage = null,
  ) => {
    const value = e.target.value;

    if (value.length > MAX_AMOUNT_INPUT_LENGTH) {
      return;
    }

    if (value.includes(".") && value.split(".")[1].length > 2) {
      return;
    }

    setter(
      isPrincipalOnlyPayment && maxValue && +value > maxValue
        ? maxValue
        : value,
    );
    if (setErrorMessage !== null) {
      checkValidity(e.target, setErrorMessage);
    }
  };

  const handleInputChangeWithDecimalValidation = (
    e,
    setter,
    setErrorMessage = null,
  ) => {
    const value = e.target.value;

    // Rejected rather than truncated, so a borrower who pastes something too long is
    // left with what they had instead of a silently clipped amount they might not read.
    if (value.length > MAX_AMOUNT_INPUT_LENGTH) {
      return;
    }

    // Validate decimal places - limit to 2 decimal places
    if (value.includes(".") && value.split(".")[1].length > 2) {
      return; // Don't update if more than 2 decimal places
    }

    // Don't format during typing - let user type naturally
    setter(value);
    if (setErrorMessage !== null) {
      checkValidity(e.target, setErrorMessage);
    }
  };

  // Function to format value on blur (when user finishes typing)
  const handleBlurWithFormatting = (e, setter, setErrorMessage = null) => {
    const value = e.target.value;

    // Format the value with proper decimal places on blur
    if (value && value !== "") {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        const formattedValue = numValue.toFixed(2);
        setter(formattedValue);
      }
    }

    if (setErrorMessage !== null) {
      checkValidity(e.target, setErrorMessage);
    }
  };

  return (
    <FlexContainer
      padding="25px 0"
      flexdirection="column"
      gap="10px"
      width={isMobile ? "100%" : "unset"}
      sx={{
        "& > div": {
          width: {
            xs: "100%",
            md: "unset",
          },
          "& > div": {
            width: {
              xs: "100%",
              md: "unset",
            },
          },
          "& > div:nth-of-type(2)": {
            width: {
              xs: "100%",
              md: "200px",
            },
          },
        },
        "& .MuiTypography-root": {
          fontSize: isMobile ? "14px" : "inherit",
        },
        "& .MuiInput-root": {
          fontSize: isMobile ? "18px" : "inherit",
          fontWeight: isMobile ? "bold" : "normal",
        },
        "& .MuiButton-root": {
          fontSize: isMobile ? "12px" : "10px",
        },
        "& .MuiTypography-caption": {
          fontSize: isMobile ? "12px" : "inherit",
        },
      }}
    >
      {(() => {
        const date = dueDate instanceof Date ? dueDate : new Date(dueDate);
        if (!dueDate || !isValid(date)) return null;
        return (
          <FormItem
            elementWidth={isMobile ? "100%" : "200px"}
            label="Actual Due Date"
            inputType="input"
            value={format(date, "MMMM dd, yyyy")}
            setValue={() => {}}
            disabled
            inputProps={{
              readOnly: true,
              style: { textAlign: "end" },
            }}
          />
        );
      })()}
      <FormItem
        elementWidth={isMobile ? "100%" : "200px"}
        label={`Amount Due (${formatCurrency(amountDue)})`}
        inputType="input"
        value={
          selectedType === ONE_TIME_PAYMENT
            ? formatCurrency(amount)
            : formatCurrency(amountDue)
        }
        setValue={(e) =>
          handleInputChangeWithDecimalValidation(
            e,
            setAmount,
            setAmountErrorMessage,
          )
        }
        inputPlaceholder="0"
        blurHandler={(e) =>
          handleBlurWithFormatting(e, setAmount, setAmountErrorMessage)
        }
        blurOnWheel
        adornment
        adornmentPosition="start"
        adornmentSymbol="$"
        disabled={
          true ||
          fieldsDisabled ||
          selectedType !== ONE_TIME_PAYMENT ||
          helocOrConstructionLoan
        }
        errorMessage={amountErrorMessage}
        hasComponent={
          !helocOrConstructionLoan &&
          selectedType === ONE_TIME_PAYMENT &&
          +amountDue > 0
        }
        component={
          <Box width={isMobile ? "100%" : "200px"}>
            <PillButton
              variant="contained"
              width={isMobile ? "100%" : "90px"}
              color="primary"
              onClick={(e) =>
                prefillInput(
                  e.target,
                  +amountDue === +amount
                    ? "0.00"
                    : padCents(amountDue.toString()),
                  setAmount,
                  setAmountErrorMessage,
                )
              }
              disabled={helocOrConstructionLoan}
              sx={{
                fontSize: "10px",
                padding: "5px 15px",
                width: isMobile ? "100%" : "90px",
              }}
            >
              {+amountDue === +amount ? "No Amount" : "Full Amount"}
            </PillButton>
          </Box>
        }
        inputProps={{
          inputMode: "decimal",
          pattern: "[0-9]*[.,]?[0-9]{0,2}",
          maxLength: MAX_AMOUNT_INPUT_LENGTH,
          type: "text",
          style: {
            textAlign: "end",
          },
        }}
      />
      {selectedType === ONE_TIME_PAYMENT ? (
        <>
          <FormItem
            elementWidth={isMobile ? "100%" : "200px"}
            label={`Late Fees (${formatCurrency(lateFeesDue)})`}
            inputType="input"
            value={mandateLateFee ? lateFeesDue : lateFees}
            setValue={(e) =>
              handleInputChangeWithDecimalValidation(
                e,
                setLateFees,
                setLateFeesErrorMessage,
              )
            }
            inputPlaceholder="0"
            blurHandler={(e) =>
              handleBlurWithFormatting(e, setLateFees, setLateFeesErrorMessage)
            }
            blurOnWheel
            adornment
            adornmentPosition="start"
            adornmentSymbol="$"
            disabled={mandateLateFee || fieldsDisabled}
            errorMessage={lateFeesErrorMessage}
            hasComponent={!mandateLateFee && +lateFeesDue > 0}
            component={
              <Box width={isMobile ? "100%" : "200px"}>
                <PillButton
                  variant="contained"
                  width={isMobile ? "100%" : "90px"}
                  color="primary"
                  onClick={(e) =>
                    prefillInput(
                      e.target,
                      padCents(lateFeesDue.toString()),
                      setLateFees,
                      setLateFeesErrorMessage,
                    )
                  }
                  disabled={+lateFeesDue === +lateFees}
                  sx={{
                    fontSize: "10px",
                    padding: "5px 15px",
                    width: isMobile ? "100%" : "90px",
                  }}
                >
                  Full Amount
                </PillButton>
              </Box>
            }
            inputProps={{
              inputMode: "decimal",
              pattern: "[0-9]*[.,]?[0-9]{0,2}",
              maxLength: MAX_AMOUNT_INPUT_LENGTH,
              type: "text",
              style: {
                textAlign: "end",
              },
            }}
          />
          <FormItem
            elementWidth={isMobile ? "100%" : "200px"}
            label={`Fees (${formatCurrency(feesDue)})`}
            inputType="input"
            value={fees}
            setValue={(e) =>
              handleInputChangeWithDecimalValidation(
                e,
                setFees,
                setFeesErrorMessage,
              )
            }
            inputPlaceholder="0"
            blurHandler={(e) =>
              handleBlurWithFormatting(e, setFees, setFeesErrorMessage)
            }
            blurOnWheel
            adornment
            adornmentPosition="start"
            adornmentSymbol="$"
            disabled={fieldsDisabled}
            errorMessage={feesErrorMessage}
            hasComponent={+feesDue > 0}
            component={
              <Box width={isMobile ? "100%" : "200px"}>
                <PillButton
                  variant="contained"
                  width={isMobile ? "100%" : "90px"}
                  color="primary"
                  onClick={(e) =>
                    prefillInput(
                      e.target,
                      padCents(feesDue.toString()),
                      setFees,
                      setFeesErrorMessage,
                    )
                  }
                  disabled={+feesDue === +fees}
                  sx={{
                    fontSize: "10px",
                    padding: "5px 15px",
                    width: isMobile ? "100%" : "90px",
                  }}
                >
                  Full Amount
                </PillButton>
              </Box>
            }
            inputProps={{
              inputMode: "decimal",
              pattern: "[0-9]*[.,]?[0-9]{0,2}",
              maxLength: MAX_AMOUNT_INPUT_LENGTH,
              type: "text",
              style: {
                textAlign: "end",
              },
            }}
          />
        </>
      ) : (
        ""
      )}
      {selectedType !== ESCROW_SHORTAGE ? (
        <>
          <FormItem
            elementWidth={isMobile ? "100%" : "200px"}
            label="Additional Principal Payment"
            inputType="input"
            value={additionalPrincipal}
            setValue={(e) =>
              handleInputChangeWithMaxValue(
                e,
                principalMaxTransaction,
                setAdditionalPrincipal,
                setAdditionalPrincipalErrorMessage,
              )
            }
            inputPlaceholder="0"
            blurHandler={(e) =>
              handleBlurWithFormatting(
                e,
                setAdditionalPrincipal,
                setAdditionalPrincipalErrorMessage,
              )
            }
            blurOnWheel
            adornment
            adornmentPosition="start"
            adornmentSymbol="$"
            disabled={
              fieldsDisabled || principalDisabled || helocOrConstructionLoan
            }
            errorMessage={additionalPrincipalErrorMessage}
            hasComponent={isPrincipalOnlyPayment && principalMaxTransaction}
            component={
              <InfoText
                elementWidth={isMobile ? "100%" : "200px"}
                Icon={InfoOutlinedIcon}
                iconColor="primary"
                text={`Maximum principal only amount is $${principalMaxTransaction}`}
                textColor="text.hint"
                textSize={10}
                textWeight="bold"
              />
            }
            inputProps={{
              inputMode: "decimal",
              pattern: "[0-9]*[.,]?[0-9]{0,2}",
              maxLength: MAX_AMOUNT_INPUT_LENGTH,
              type: "text",
              style: {
                textAlign: "end",
              },
            }}
          />
          {additionalEscrowAmountAllowed ? (
            <FormItem
              elementWidth={isMobile ? "100%" : "200px"}
              label="Additional Escrow Payment"
              inputType="input"
              value={additionalEscrow}
              setValue={(e) =>
                handleInputChangeWithDecimalValidation(
                  e,
                  setAdditionalEscrow,
                  setAdditionalEscrowErrorMessage,
                )
              }
              inputPlaceholder="0"
              blurHandler={(e) =>
                handleBlurWithFormatting(
                  e,
                  setAdditionalEscrow,
                  setAdditionalEscrowErrorMessage,
                )
              }
              blurOnWheel
              adornment
              adornmentPosition="start"
              adornmentSymbol="$"
              disabled={fieldsDisabled}
              errorMessage={additionalEscrowErrorMessage}
              hasComponent
              component={
                <InfoText
                  elementWidth={isMobile ? "100%" : "200px"}
                  Icon={InfoOutlinedIcon}
                  iconColor="primary"
                  text="No escrow audit shortages allowed"
                  textColor="text.hint"
                  textSize={10}
                  textWeight="bold"
                />
              }
              inputProps={{
                inputMode: "decimal",
                pattern: "[0-9]*[.,]?[0-9]{0,2}",
                maxLength: MAX_AMOUNT_INPUT_LENGTH,
                type: "text",
                style: {
                  textAlign: "end",
                },
              }}
            />
          ) : (
            ""
          )}
        </>
      ) : (
        ""
      )}
    </FlexContainer>
  );
}
